import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { db } from '../lib/db'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import NotificationBell from '../components/NotificationBell'
import { renderActivityCard } from '../lib/activityCard'
import { groupActivityByRound } from '../lib/activityGroup'
import { readActivityScope } from '../lib/activityScope'
import { readCache, writeCache, TTL } from '../lib/cache'
import { classifyActiveRound, parseActiveRoundRow, createActiveRoundPoll, classifyRoundLiveness } from '../lib/activeRound'
import { buildPrefillFromRound, buildPrefillFromLeagueRound, stashPrefill } from '../lib/scheduledRound'
import { deriveEventStatus } from '../lib/golf'

// Per-player cache keys. Suffixed with player.id so a device that switches
// accounts doesn't render the previous user's tiles for a frame. Two
// separate namespaces so a stale scheduled-rounds cache from last month
// doesn't share a TTL with a much fresher recent-activity blob.
const cacheKeys = (pid) => ({
  bootstrap: `home:bootstrap:${pid}`,
  scheduled: `home:scheduled:${pid}`,
  tfIndex:   `home:tf_index:${pid}`,
  activity:  `home:activity:${pid}`,
  league:    `home:league:${pid}`,
})

function formatRoundDate(dateVal) {
  if (!dateVal) return ''
  // postgres.js can return DATE columns as JS Date objects; normalize first
  let s = dateVal
  if (dateVal instanceof Date) s = dateVal.toISOString().split('T')[0]
  else if (typeof dateVal === 'string' && dateVal.includes('T')) s = dateVal.split('T')[0]
  const d = new Date(s + 'T00:00:00')
  if (isNaN(d.getTime())) return ''
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

// Short M/D format for recent-activity rows. Same timezone handling as
// formatRoundDate but parses at local noon (not midnight) so the displayed
// date matches the played date even if a DATE column comes back as a UTC
// timestamp near the day boundary. Without this, "2026-06-17" returned by
// postgres.js parses as UTC midnight and displays as 6/16 in any
// negative-offset timezone (i.e., everywhere in the Americas).
function formatPlayedDate(dateVal) {
  if (!dateVal) return ''
  let s = dateVal
  if (dateVal instanceof Date) s = dateVal.toISOString().split('T')[0]
  else if (typeof dateVal === 'string' && dateVal.includes('T')) s = dateVal.split('T')[0]
  const d = new Date(s + 'T12:00:00')
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
}

function formatTime12(t) {
  if (!t) return ''
  const [h, m] = String(t).split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

export default function HomePage() {
  const { player, isCommissioner } = useAuth()
  const navigate = useNavigate()

  // Per-tile state, each seeded from the cache on first render so tiles
  // paint instantly rather than waiting on a network round-trip. On a
  // fresh install (no cache) each initializer returns the empty/null
  // default the tile already knew how to render — same visual outcome
  // as the pre-cache path.
  const keys = player?.id ? cacheKeys(player.id) : null
  const [liveEvent, setLiveEvent] = useState(() => {
    if (!keys) return null
    const cached = readCache(keys.bootstrap)
    return cached?.value?.live_event || null
  })
  const [activeRound, setActiveRound] = useState(() => {
    // Two sources for the resume card:
    //   • localStorage 'active_round' — the ScorecardPage's own
    //     write-through cache from THIS device.
    //   • bootstrap cache — snapshot of the server's active_rounds row
    //     (which reflects the most recent write across ALL devices).
    // On a multi-device user, either can be fresher. Compare timestamps
    // and pick the newer one so a round finished on Device A doesn't
    // resurrect via a stale Device B localStorage snapshot.
    let localVal = null, localAt = 0
    try {
      localVal = JSON.parse(localStorage.getItem('active_round') || 'null')
      // ScorecardPage writes { scores, currentHole, ...standaloneState };
      // no timestamp today, so fall back to the cache-side comparison as
      // a floor. Future ScorecardPage saves can stamp `updated_at` and
      // this compare will get sharper.
      localAt = Number(localVal?.updated_at) || 0
    } catch {}
    if (!keys) return localVal
    const cached = readCache(keys.bootstrap)
    const cacheVal = cached?.value?.active_round || null
    const cacheAt = Number(cached?.storedAt) || 0
    if (localVal && cacheVal) {
      return cacheAt > localAt ? cacheVal : localVal
    }
    return localVal || cacheVal
  })
  const [upcomingRounds, setUpcomingRounds] = useState(() => {
    if (!keys) return []
    const cached = readCache(keys.scheduled)
    return Array.isArray(cached?.value) ? cached.value : []
  })
  const [tfIndex, setTfIndex] = useState(() => {
    if (!keys) return { overall: null, attested: null }
    const cached = readCache(keys.tfIndex)
    return cached?.value || { overall: null, attested: null }
  })
  const [actionRound, setActionRound] = useState(null)
  // F-080: the player's startable league rounds — their next-unplayed-week
  // pairing per league, pre-mapped into a PlayPage prefill (course + which-nine
  // + league linkage). Empty when the player is in no leagues, has no unplayed
  // week, or the commissioner hasn't paired them yet. Cached like the other
  // Home tiles so it paints instantly on cold-open.
  const [leagueRounds, setLeagueRounds] = useState(() => {
    if (!keys) return []
    const cached = readCache(keys.league)
    return Array.isArray(cached?.value) ? cached.value : []
  })
  const [recentActivity, setRecentActivity] = useState(() => {
    if (!keys) return []
    const cached = readCache(keys.activity)
    return Array.isArray(cached?.value) ? cached.value : []
  })
  // Per-tile refreshing hint — replaces the old global `loading` gate.
  // Never blocks the render tree; the header spinner uses it to show
  // "syncing" without hiding the tiles.
  const [refreshing, setRefreshing] = useState({
    bootstrap: false, scheduled: false, tfIndex: false, activity: false, league: false,
  })
  // Kept for back-compat with the existing JSX — no longer gates the
  // render tree, but preserved because a couple of downstream tiles
  // key their empty-state copy off it.
  const loading = Object.values(refreshing).some(Boolean)

  // Authoritative active-round reconcile (ops #42 + #43). Runs on every
  // navigation-to-Home (HomePage is a route element, so it remounts each
  // visit and the mount effect below re-invokes this), on the ~30s poll, and
  // on every foreground transition — so a deleted round clears and a
  // newly-started round appears WITHOUT a manual refresh.
  //
  // Discriminated clear (goal b): the tile + localStorage 'active_round' are
  // cleared ONLY when the server POSITIVELY reports no round (classify →
  // 'empty'), from EITHER home-bootstrap OR the /db fallback. A transient
  // error (classify → 'unknown') NEVER clears — it leaves the current card
  // exactly as-is, so a blip can't wipe a real resume tile and can't leave a
  // dead one showing. This is why home-bootstrap now returns `undefined` (not
  // null) on a query error, and why the fallback is settled per-call below.
  // `silent` skips the visible "Refreshing…" spinner toggle. The first mount
  // reconcile surfaces the spinner (the user asked to see Home load); the ~30s
  // poll and every foreground reconcile run SILENTLY so an idle user staring at
  // Home doesn't watch a spinner blink every 30s (ops #43 wanted a background
  // poll, not a recurring UI artifact). Only the refreshing.bootstrap toggle is
  // gated — the authoritative clear/keep logic runs identically either way.
  const reconcileActiveRound = useCallback(async ({ silent = false } = {}) => {
    if (!player?.id) return
    const k = cacheKeys(player.id)
    if (!silent) setRefreshing(r => ({ ...r, bootstrap: true }))
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) return

      let data = null
      let fromBootstrap = false
      let eventAnswered = false
      try {
        const res = await fetch('/.netlify/functions/home-bootstrap', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({}),
          // Stop waiting after 6s. Feature-detected: Safari <16 and older
          // Android WebViews lack AbortSignal.timeout, and a synchronous
          // TypeError here would wrongly route EVERY load (even healthy ones)
          // on those devices to the fallback.
          ...(typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
            ? { signal: AbortSignal.timeout(6000) }
            : {}),
        })
        if (res.ok) {
          data = (await res.json())?.data ?? null
          fromBootstrap = true
          eventAnswered = true // home-bootstrap always answers current_event
        }
      } catch { /* abort / network failure — fall through to /db fallback */ }

      if (!fromBootstrap) {
        // home-bootstrap timed out, threw, or returned non-OK — fall back to
        // the /db proxy that has always answered in <400ms. SETTLED per-call
        // (client-side HTTP; NOT the postgres.js single-client concurrency
        // rule — two separate function invocations) so a get_active_round
        // that succeeds-with-null still authoritatively clears while a thrown
        // get_active_round stays 'unknown' (keep). If BOTH throw we bail and
        // the finally keeps everything on screen (a sick backend must never
        // wipe a real resume card — #239/F-050).
        const [arRes, evRes] = await Promise.allSettled([
          db('get_active_round', { player_id: player.id }),
          db('get_current_event', {}),
        ])
        if (arRes.status === 'rejected' && evRes.status === 'rejected') return
        const arRaw = arRes.status === 'fulfilled' ? (arRes.value?.data ?? null) : undefined
        eventAnswered = evRes.status === 'fulfilled'
        const evData = eventAnswered ? (evRes.value?.data ?? null) : null
        data = { current_event: evData, active_round: arRaw }
      }

      const ev = data?.current_event
      // Show the LIVE tile while an outing is actively being scored (D-341).
      // deriveEventStatus keeps in_progress => live coherent for callers that
      // load scores; get_current_event doesn't return per-event round_scores, so
      // (the dropped events.status active-round pointer is gone) also treat the
      // outing as live when TODAY falls inside its play window — the schedule
      // dates come from event_rounds (D-341 Phase-3 cutover).
      const derivedStatus = ev ? deriveEventStatus(ev, ev.event_rounds || [], []) : null
      const playDates = (ev?.event_rounds || []).map(r => r && r.play_date).filter(Boolean)
      const inPlayWindow = ev && ev.completed_at == null && playDates.length > 0 && (() => {
        const first = playDates.reduce((a, b) => (b < a ? b : a))
        const last = playDates.reduce((a, b) => (b > a ? b : a))
        const now = Date.now()
        return now >= Date.parse(`${first}T00:00:00`) && now <= Date.parse(`${last}T23:59:59`)
      })()
      const live = ev && (derivedStatus === 'in_progress' || inPlayWindow) ? ev : null
      if (eventAnswered) setLiveEvent(live)

      const outcome = classifyActiveRound(data?.active_round)
      let activeRoundData = null
      if (outcome === 'found') {
        // Creator-delete handoff (D-279). A soft-deleted round still has its
        // active_rounds row, so distinguish LIVE / DELETED_GRABBABLE / DEAD via
        // the joined deleted_at before painting the tile. DEAD (soft-deleted past
        // the window) is treated like 'empty' — clear it. DELETED_GRABBABLE
        // paints a "host ended the round — continue?" tile (grab-from-Home for
        // the departing player who reopens the app within the window).
        const liveness = classifyRoundLiveness({ row: data.active_round })
        if (liveness === 'DEAD') {
          setActiveRound(null)
          try { localStorage.removeItem('active_round') } catch {}
        } else {
          activeRoundData = parseActiveRoundRow(data.active_round)
          if (activeRoundData) {
            activeRoundData.grabbable = liveness === 'DELETED_GRABBABLE'
            // Only cache a LIVE round for cold-open auto-resume; a grabbable one
            // must not silently resume into a not-yet-grabbed (dead) scorecard.
            try {
              if (liveness === 'LIVE') localStorage.setItem('active_round', JSON.stringify(activeRoundData))
              else localStorage.removeItem('active_round')
            } catch {}
            setActiveRound(activeRoundData)
          }
        }
      } else if (outcome === 'empty') {
        // Server POSITIVELY reports no active round → this device is stale.
        // Clear tile + localStorage immediately so the resume card disappears
        // with no manual refresh (the exact bug #1 in ops #42).
        setActiveRound(null)
        try { localStorage.removeItem('active_round') } catch {}
      }
      // outcome === 'unknown' → could not confirm → leave tile + localStorage
      // untouched.

      // Only refresh the combined bootstrap cache on an authoritative answer,
      // so 'unknown' keeps the last-known warm-paint blob intact.
      if (eventAnswered && (outcome === 'found' || outcome === 'empty')) {
        writeCache(k.bootstrap, { live_event: live, active_round: activeRoundData }, TTL.FIFTEEN_MIN)
      }
    } catch { /* silent — cached value stays on screen */ }
    finally { if (!silent) setRefreshing(r => ({ ...r, bootstrap: false })) }
  }, [player?.id])

  useEffect(() => {
    if (!player?.id) return

    // Resume-to-GPS auto-navigation on the CACHED active_round — one
    // less blocker before we can jump into the scorecard. Same guard
    // rails as before (session breadcrumb, one-shot).
    try {
      const gpsWasOpen = localStorage.getItem('tf_gps_open') === '1'
      const consumed = sessionStorage.getItem('tf_autonav_consumed') === '1'
      if (activeRound && !activeRound.grabbable && gpsWasOpen && !consumed) {
        sessionStorage.setItem('tf_autonav_consumed', '1')
        navigate('/scorecard', { state: { ...activeRound, standalone: true } })
        return
      }
    } catch {}

    const k = cacheKeys(player.id)

    // Fan out all four background reads in parallel. Each writes its
    // slice of state as soon as it resolves — no barrier, no shared
    // loading flag. On a warm cache the tiles were already painted
    // above; these just refresh them.

    // 1) Active-round + live-event reconcile. Extracted to reconcileActiveRound
    //    so the exact same authoritative check runs on every navigation-to-Home
    //    (this mount), on the ~30s poll, and on foreground (ops #42 goal a +
    //    #43). It carries the 6s timeout + /db fallback + discriminated clear.
    reconcileActiveRound()

    // 2) Scheduled rounds — its own function (auth-scoped roster).
    setRefreshing(r => ({ ...r, scheduled: true }))
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/.netlify/functions/scheduled-rounds', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        if (!res.ok) return
        const data = await res.json()
        const rounds = data.rounds || []
        setUpcomingRounds(rounds)
        writeCache(k.scheduled, rounds, TTL.FIFTEEN_MIN)
      } catch {}
      finally { setRefreshing(r => ({ ...r, scheduled: false })) }
    })()

    // 3) TF Index — expensive compute; the response includes the
    //    freshly written players.tf_index_* columns. Long TTL is fine
    //    because the value only changes when a round is saved.
    setRefreshing(r => ({ ...r, tfIndex: true }))
    ;(async () => {
      try {
        const res = await fetch(`/.netlify/functions/compute-tf-index?player_id=${player.id}`)
        if (!res.ok) return
        const data = await res.json()
        const next = { overall: data.overall, attested: data.attested }
        setTfIndex(next)
        writeCache(k.tfIndex, next, TTL.HOUR)
      } catch {}
      finally { setRefreshing(r => ({ ...r, tfIndex: false })) }
    })()

    // 4) Recent activity — 10 most recent rounds for me + shared players.
    setRefreshing(r => ({ ...r, activity: true }))
    ;(async () => {
      try {
        // Scope the Home feed to the profile preference set on /me (D-277):
        // 'all' (Me + Friends, default) | 'just_me' (Me only) | 'my_friends'
        // (Friends only). Read here (not from state) so returning from /me —
        // which remounts Home and re-runs this effect — picks up the fresh
        // per-device value. ActivityPage keeps its own independent dropdown.
        const { data: activity } = await db('get_recent_activity', { player_id: player.id, filter: readActivityScope(), limit: 10 })
        const list = Array.isArray(activity) ? activity : []
        setRecentActivity(list)
        writeCache(k.activity, list, TTL.FIFTEEN_MIN)
      } catch {}
      finally { setRefreshing(r => ({ ...r, activity: false })) }
    })()

    // 5) League rounds (F-080) — the player's leagues + their next-unplayed-
    //    week pairing, pre-mapped into a PlayPage prefill so "Start round"
    //    lands on /play with course + which-nine + league linkage filled in.
    //    Self-contained + fully guarded: any failure leaves the tile empty and
    //    never disturbs the rest of Home. Each league's pairing lookup is a
    //    SEPARATE db() HTTP call, run sequentially (one per league with an
    //    unplayed week) — small N, and it keeps the load off the bootstrap.
    setRefreshing(r => ({ ...r, league: true }))
    ;(async () => {
      try {
        const { data: leagues } = await db('get_player_leagues', { player_id: player.id })
        const list = Array.isArray(leagues) ? leagues : []
        const startable = []
        for (const lg of list) {
          if (lg.next_unplayed_week == null) continue
          const { data: pairing } = await db('get_league_pairing_for_player', {
            player_id: player.id, event_id: lg.id, week_number: lg.next_unplayed_week,
          })
          if (!pairing?.league_round_id) continue
          const prefill = buildPrefillFromLeagueRound(pairing)
          if (!prefill) continue
          startable.push({
            key: `${lg.id}:${pairing.week_number}`,
            event_name: lg.name,
            week_number: pairing.week_number,
            course_name: pairing.course_name || null,
            scheduled_date: pairing.scheduled_date || null,
            opponent_name: pairing.player_a_id === player.id
              ? pairing.player_b_name
              : pairing.player_a_name,
            prefill,
          })
        }
        setLeagueRounds(startable)
        writeCache(k.league, startable, TTL.FIFTEEN_MIN)
      } catch {}
      finally { setRefreshing(r => ({ ...r, league: false })) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id])

  // Auto-refresh the active-round check while Home is on screen (ops #43).
  // Polls ONLY the active-round reconcile (NOT scheduled/tfIndex/activity) on
  // a ~30s interval so a round going live elsewhere (a commissioner starting
  // an event round you're in) appears on its own. Paused while the tab is
  // backgrounded; an immediate reconcile fires on foreground. HomePage remounts
  // per navigation, so this poll only runs while the user is actually on Home.
  useEffect(() => {
    if (!player?.id) return
    const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'
    const poll = createActiveRoundPoll({
      intervalMs: 30000,
      isHidden,
      // Silent — the interval + foreground reconciles run in the background
      // without flashing the "Refreshing…" spinner (ops #43).
      onTick: () => { reconcileActiveRound({ silent: true }) },
    })
    const onVis = () => poll.handleVisibilityChange()
    document.addEventListener('visibilitychange', onVis)
    poll.start()
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      poll.stop()
    }
  }, [player?.id, reconcileActiveRound])

  const removeRound = async (id) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      await fetch(`/.netlify/functions/scheduled-rounds?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })
      setUpcomingRounds(prev => prev.filter(r => r.id !== id))
    } catch {}
  }

  // Creator-delete handoff (D-279). The departing player (or any co-player who
  // reopens Home within the window) taps the "host ended the round" tile to grab
  // ownership. First-write-wins server-side: on 'already_continued' the round is
  // alive under someone else, so just reconcile the tile away and stay on Home.
  const grabAndResume = async () => {
    if (!activeRound?.id) return
    try {
      const res = await db('grab_round', { standalone_round_id: activeRound.id })
      if (res?.error === 'already_continued') {
        setActiveRound(null)
        reconcileActiveRound({ silent: true })
        return
      }
      navigate('/scorecard', { state: { ...activeRound, standalone: true } })
    } catch {
      // Network hiccup — leave the tile; the next poll re-evaluates.
    }
  }

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
  }

  return (
    <div className="page">
      <div className="container">
        {/* Top bar: TF Golf (left) · greeting (page-centered) · profile (right) */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 0, paddingBottom: 0, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <img src="/tf-shield.webp" alt="TournyFlex" style={{ width: 34, height: 'auto' }} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.01em' }}>Golf</span>
          </div>
          <p style={{ position: 'absolute', left: 56, right: 56, textAlign: 'center', margin: 0, pointerEvents: 'none', fontSize: '1rem', fontFamily: 'var(--font-body)', color: 'var(--gray-300)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {greeting()}{player ? `, ${player.first_name || player.name?.split(' ')[0] || ''}` : ''}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <NotificationBell playerId={player?.id} />
            <button
              onClick={() => navigate('/me')}
              aria-label="Profile"
              style={{ width: 38, height: 38, flexShrink: 0, borderRadius: '50%', border: '1.5px solid var(--gold)', background: 'var(--green-deep)', color: 'var(--gold)', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {(player?.first_name || player?.name || '?').trim().charAt(0).toUpperCase()}
            </button>
          </div>
        </div>

        {loading && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, padding: '8px 0 0', fontSize: '0.7rem', color: 'var(--gray-400)',
          }}>
            <div className="spinner" style={{ width: 12, height: 12 }} />
            <span>Refreshing…</span>
          </div>
        )}

        {(
          <>
            {/* Live event card */}
            {liveEvent && (
              <div
                className="home-progress-card"
                onClick={() => navigate('/leaderboard')}
              >
                <div className="home-progress-body">
                  <span className="home-progress-pill">LIVE</span>
                  <h3 className="home-progress-title">{liveEvent.name || 'Golf Outing'}</h3>
                  <p className="home-progress-sub">Tap to view leaderboard</p>
                </div>
                {liveEvent.year != null && <span className="home-progress-type">{liveEvent.year}</span>}
                <div className="home-progress-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                </div>
              </div>
            )}

            {/* Active round (in progress) — or a creator-ended round the user can grab (D-279) */}
            {activeRound && (
              <div
                className="home-progress-card"
                onClick={activeRound.grabbable ? grabAndResume : () => navigate('/scorecard', { state: { ...activeRound, standalone: true } })}
              >
                <div className="home-progress-body">
                  <span className="home-progress-pill">{activeRound.grabbable ? 'ENDED BY HOST' : 'IN PROGRESS'}</span>
                  <h3 className="home-progress-title">{activeRound.course?.name || 'Round'}</h3>
                  <p className="home-progress-sub">{activeRound.grabbable ? 'Host ended the round — tap to keep scoring' : 'Tap to resume scoring'}</p>
                </div>
                {activeRound.roundType && <span className="home-progress-type">{activeRound.roundType}</span>}
                <div className="home-progress-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                </div>
              </div>
            )}

            {/* Upcoming rounds */}
            {upcomingRounds.length > 0 && (
              <>
                <p className="home-section-label">Upcoming Rounds</p>
                {upcomingRounds.map(r => (
                  <div
                    key={r.id}
                    className="home-round-card"
                    onClick={() => setActionRound(r)}
                  >
                    <div className="home-round-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                    </div>
                    <div className="home-round-body">
                      <div className="home-round-title">{r.club_name || r.course_name || 'TBD'}</div>
                      <div className="home-round-meta-grid">
                        <span><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>{formatRoundDate(r.date)}</span>
                        <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 22V4" /><path d="M4 4l13 3-13 3" /></svg>{r.holes} holes</span>
                        <span><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>{formatTime12(r.tee_time)}</span>
                        <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>{r.player_count} {r.player_count === 1 ? 'player' : 'players'}</span>
                      </div>
                    </div>
                    <div className="home-round-chevron" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18" /></svg>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* League rounds (F-080) — the player's next unplayed week per
                league, ready to start with course + which-nine pre-filled.
                Tapping Start stashes the league prefill (survives the F-074
                SW-update reload, same transport as scheduled rounds) and lands
                on /play; the started round links to league standings via
                league_round_id. */}
            {leagueRounds.length > 0 && (
              <>
                <p className="home-section-label">League Rounds</p>
                {leagueRounds.map(lr => (
                  <button
                    key={lr.key}
                    className="home-round-card"
                    type="button"
                    style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                    onClick={() => {
                      stashPrefill(lr.prefill)
                      navigate('/play', { state: { prefill: lr.prefill } })
                    }}
                  >
                    <div className="home-round-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="M6 9H4.5a2.5 2.5 0 010-5H6" /><path d="M18 9h1.5a2.5 2.5 0 000-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0012 0V2Z" /></svg>
                    </div>
                    <div className="home-round-body">
                      <div className="home-round-title">{lr.event_name || 'League'}</div>
                      <div className="home-round-meta-grid">
                        <span><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>Week {lr.week_number}</span>
                        {lr.course_name && (
                          <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 22V4" /><path d="M4 4l13 3-13 3" /></svg>{lr.course_name}</span>
                        )}
                        {lr.opponent_name && (
                          <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>vs {lr.opponent_name}</span>
                        )}
                      </div>
                    </div>
                    <div className="home-round-chevron" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18" /></svg>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* TournyFlex Index — two columns, info icon top-right, no shield centerpiece */}
            <div className="home-index-card">
              <div className="home-index-header">
                <p className="home-index-label">TournyFlex Index</p>
                <button className="home-index-info" aria-label="About TournyFlex Index" type="button">
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                </button>
              </div>
              <div className="home-index-row">
                <div className="home-index-col">
                  <p className="home-index-col-label">Overall</p>
                  <p className="home-index-num">
                    {tfIndex.overall != null
                      ? Number(tfIndex.overall).toFixed(1)
                      : (player?.starter_handicap_index != null ? Number(player.starter_handicap_index).toFixed(1) : '—')}
                  </p>
                </div>
                <div className="home-index-col">
                  <p className="home-index-col-label">Attested</p>
                  <p className={`home-index-num${tfIndex.attested == null ? ' dim' : ''}`}>
                    {tfIndex.attested != null ? Number(tfIndex.attested).toFixed(1) : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Schedule a Round — two-line row CTA */}
            <button
              className="home-cta-row"
              onClick={() => navigate('/schedule')}
              type="button"
            >
              <div className="home-cta-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
              </div>
              <div className="home-cta-body">
                <div className="home-cta-title">Schedule a Round</div>
                <div className="home-cta-sub">Find a course, invite players</div>
              </div>
              <div className="home-cta-chevron" aria-hidden="true">
                <svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18" /></svg>
              </div>
            </button>

            {/* Action tiles — Create Event + Set up League in a 2-column row.
                LOCKED 2026-07-05: title is always "Create Event" (fits the tile);
                subtitle "Plan a one-time outing" carries the semantic. Server-side
                role gate still enforces who can actually create at submit time. */}
            <div className="home-action-grid">
              <button
                className="home-action-tile"
                onClick={() => navigate('/events/new')}
                type="button"
              >
                <div className="home-action-icon" aria-hidden="true">🏌️</div>
                <div className="home-action-body">
                  <div className="home-action-title">Create Event</div>
                  <div className="home-action-sub">Plan a one-time outing</div>
                </div>
              </button>
              <button
                className="home-action-tile"
                onClick={() => navigate('/events/new?type=league')}
                type="button"
              >
                <div className="home-action-icon" aria-hidden="true">🏆</div>
                <div className="home-action-body">
                  <div className="home-action-title">Set up League</div>
                  <div className="home-action-sub">Start a season</div>
                </div>
              </button>
            </div>

            {isCommissioner && (
              <button
                className="home-action-row home-action-row--subtle"
                onClick={() => navigate('/events')}
                type="button"
              >
                <div className="home-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                </div>
                <div className="home-action-body">
                  <div className="home-action-title">My Events</div>
                  <div className="home-action-sub">View drafts and published events</div>
                </div>
                <div className="home-action-chevron" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18" /></svg>
                </div>
              </button>
            )}

            {/* Recent Activity — shared renderer with ActivityPage. */}
            {recentActivity.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 24 }}>
                  <p className="home-section-label" style={{ margin: 0 }}>Recent Activity</p>
                  <button
                    type="button"
                    onClick={() => navigate('/activity')}
                    style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '2px 4px' }}
                  >
                    See all ›
                  </button>
                </div>
                <div className="home-activity-group">
                  {groupActivityByRound(recentActivity, player?.id).map((r, i) => renderActivityCard({ r, i, player, navigate }))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {actionRound && (
        <div
          onClick={() => setActionRound(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 20px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--green-dark)',
              border: '1px solid var(--green-mid)',
              borderRadius: 'var(--radius-lg)',
              padding: '20px',
              maxWidth: 320, width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <p style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
              {actionRound.club_name || actionRound.course_name || 'Round'}
            </p>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-300)', marginBottom: 18 }}>
              {formatRoundDate(actionRound.date)} · {formatTime12(actionRound.tee_time)}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                className="btn btn-primary btn-full"
                onClick={() => {
                  const r = actionRound
                  setActionRound(null)
                  // Build the full prefill (course + 9/18 + which-nine + roster)
                  // and stash it BEFORE navigating so it survives the F-074
                  // SW-update reload / WebView remount that drops in-memory
                  // router state on this Home→/play hop (F-079 root cause).
                  const prefill = buildPrefillFromRound(r)
                  stashPrefill(prefill)
                  navigate('/play', { state: { prefill } })
                }}
                style={{ padding: '12px 16px' }}
              >
                Start Round
              </button>
              <button
                className="btn btn-ghost btn-full"
                onClick={async () => {
                  const id = actionRound.id
                  setActionRound(null)
                  await removeRound(id)
                }}
                style={{ padding: '12px 16px', color: '#ff8080' }}
              >
                Remove
              </button>
              <button
                className="btn btn-ghost btn-full"
                onClick={() => setActionRound(null)}
                style={{ padding: '12px 16px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
